import { useCallback, useEffect, useRef, useState, type CSSProperties, type FormEvent, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Trash2, Check, X, ImagePlus, Image as ImageIcon, Lock as LockIcon, Upload } from 'lucide-react';
import { useAppState } from '../hooks/useAppState';
import { MarathonView } from '../components/ledger/Marathon';
import { ProgressionBoard } from '../components/ledger/ProgressionBoard';
import { RoutineBuilder, RoutineView } from '../components/ledger/Routine';
import { SkinPicker } from '../components/guild/SkinPicker';
import { authApi, engineApi, statFieldsApi, strategiesApi, type StrategyDef, tradesApi, type StatField, type StatFieldDef, type StatFieldType, type ChartDraft, type NewTrade, type Trade, type TradeMode, type TradeStrategy, type TradeSummary, type TradeStats, type StrategyReadiness } from '../lib/api';
import { EquityCurve, OutcomeHistogram, ReachCurve, Streaks, type ChartTrade } from '../components/ledger/Charts';
import { INK, PANEL, RIM, ICE, TEAL, WIN, LOSS, TITLE_FONT, PX_FONT, pixelFrame, arcadeTitleShadow, ACCENT, FIELD, GOLD, NUM_FONT, PAGE, PANEL_GLASS, CASE, ON_ACCENT, FIELD_SHADOW, TAB_ON, TAB_OFF, TITLE_WEIGHT, HEAD_SHADOW, SOFT_TEXT } from '../components/guild/theme';
import { useSkinCopy } from '../hooks/useSkinCopy';
import { csvFromUpload } from '../lib/unzipCsv';
import { POINT_VALUES } from '../lib/pointValues';
import { SwapWord } from '../components/guild/SwapWord';

const MODES: TradeMode[] = ['backtest', 'paper', 'live'];
/** Backtests are bar-replay sessions in the Readiness Progression vocabulary */
const modeName = (m: TradeMode) => (m === 'backtest' ? 'replay' : m);
/** Readiness Progression holds replay + paper; the Trade Log is live only; the standalone page shows all */
export type TrackerTrack = 'readiness' | 'live' | 'all';
const TRACK_MODES: Record<TrackerTrack, TradeMode[]> = { readiness: ['backtest', 'paper'], live: ['live'], all: MODES };
const SESSIONS = ['Asia', 'London', 'NY AM', 'NY PM'];

const label: CSSProperties = { fontFamily: PX_FONT, fontSize: 10, letterSpacing: '0.16em', textTransform: CASE, color: TEAL };
const inputStyle: CSSProperties = {
  width: '100%', background: FIELD, color: ICE, border: 'none', padding: '9px 10px',
  fontFamily: PX_FONT, fontSize: 12, letterSpacing: '0.06em',
  boxShadow: FIELD_SHADOW, outline: 'none',
};

const pct = (n: number) => `${Math.round(n * 100)}%`;
const r = (n: number) => `${n >= 0 ? '+' : ''}${n.toFixed(2)}R`;
const today = () => {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 10);
};

function Panel({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  return <section style={{ background: PANEL_GLASS, padding: 20, boxShadow: pixelFrame(), ...style }}>{children}</section>;
}

function Segmented<T extends string>({ value, options, onChange, format = (o: T) => o }: {
  value: T; options: T[]; onChange: (v: T) => void; format?: (o: T) => string;
}) {
  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
      {options.map(o => {
        const on = o === value;
        return (
          <button
            key={o}
            type="button"
            onClick={() => onChange(o)}
            style={{
              fontFamily: PX_FONT, fontSize: 11, letterSpacing: '0.12em', textTransform: CASE,
              padding: '7px 11px', border: 'none', cursor: 'pointer',
              background: on ? ACCENT : FIELD, color: on ? ON_ACCENT : TEAL,
              boxShadow: `0 0 0 2px ${on ? INK : RIM}`,
            }}
          >
            {format(o)}
          </button>
        );
      })}
    </div>
  );
}

function Stat({ name, value, tone }: { name: string; value: string; tone?: string }) {
  return (
    <div style={{ minWidth: 0, containerType: 'inline-size', background: PANEL, padding: '12px 14px', boxShadow: `0 0 0 2px ${RIM}, 0 0 0 4px ${INK}` }}>
      <div style={{ ...label, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</div>
      {/* Scales with the tile's own width (container units) so long values like +41.84R always fit whole */}
      <div style={{ fontFamily: NUM_FONT, fontSize: 'min(28px, 20cqw)', lineHeight: 1.15, whiteSpace: 'nowrap', color: tone ?? ICE, marginTop: 4 }}>{value}</div>
    </div>
  );
}

function StatStrip({ s }: { s: TradeStats }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 12 }}>
      <Stat name="Trades" value={String(s.trades)} />
      <Stat name="Win rate" value={s.trades ? pct(s.winRate) : '—'} />
      <Stat name="Avg win" value={s.wins ? r(s.avgWinnerR) : '—'} tone={WIN} />
      <Stat name="Avg loss" value={s.trades > s.wins ? r(-s.avgLoserR) : '—'} tone={LOSS} />
      <Stat name="Expectancy" value={s.trades ? r(s.expectancyR) : '—'} tone={s.expectancyR >= 0 ? WIN : LOSS} />
      <Stat name="Total" value={s.trades ? r(s.totalR) : '—'} tone={s.totalR >= 0 ? WIN : LOSS} />
      <Stat name="Avg max run" value={s.trades ? r(s.avgMaxRunR) : '—'} />
      <Stat name="Rules kept" value={s.trades ? pct(s.ruleAdherence) : '—'} />
    </div>
  );
}

function RefitBar({ strategy, summary }: { strategy: TradeStrategy; summary: TradeSummary }) {
  const st = summary.byStrategy[strategy];
  // Summary can briefly belong to the previous tab (e.g. right after creating a strategy)
  if (!st) return null;
  const done = summary.refitEvery - st.tradesUntilRefit;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap', background: PANEL, padding: '12px 16px', boxShadow: `0 0 0 2px ${RIM}, 0 0 0 4px ${INK}` }}>
      <span style={{ ...label, color: st.currentTier ? GOLD : TEAL }}>{st.currentTier ?? 'No tier yet'}</span>
      <div style={{ display: 'flex', gap: 3, flex: '1 1 240px' }} aria-label={`${done} of ${summary.refitEvery} trades toward next re-fit`}>
        {Array.from({ length: summary.refitEvery }, (_, i) => (
          <span key={i} style={{ flex: 1, height: 10, background: i < done ? ACCENT : FIELD, boxShadow: `0 0 0 1px ${INK}` }} />
        ))}
      </div>
      <span style={{ ...label, fontSize: 9 }}>
        Re-fit in {st.tradesUntilRefit} paper/live trade{st.tradesUntilRefit === 1 ? '' : 's'}
        {st.lastRefit && ` · last at ${st.lastRefit.trade_count}`}
      </span>
    </div>
  );
}

function ChartCard({ title, hint, children }: { title: string; hint: string; children: ReactNode }) {
  return (
    <Panel style={{ minWidth: 0 }}>
      <div><span className="pal-chapter" style={{ fontFamily: TITLE_FONT, fontSize: 20, color: ICE, textShadow: HEAD_SHADOW }}>{title}</span></div>
      <div style={{ ...label, fontSize: 9, letterSpacing: '0.1em', margin: '4px 0 14px' }}>{hint}</div>
      {children}
    </Panel>
  );
}

/** Progress toward a viable prescription for this strategy: the Readiness Progression milestones */
function MilestoneBar({ name, have, need, expectancy }: { name: string; have: number; need: number; expectancy: number }) {
  const done = have >= need && expectancy > 0;
  return (
    <div style={{ flex: '1 1 260px', display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
        <span style={{ ...label, color: done ? WIN : ICE }}>{done ? '✓ ' : ''}{name}</span>
        <span style={{ ...label, fontSize: 9 }}>{Math.min(have, need)}/{need} · {expectancy >= 0 ? '+' : ''}{expectancy.toFixed(2)}R exp.</span>
      </div>
      <div style={{ height: 8, background: FIELD, borderRadius: 4, overflow: 'hidden' }}>
        <div style={{ width: `${Math.min(1, have / need) * 100}%`, height: '100%', background: done ? WIN : ACCENT, transition: 'width 0.4s' }} />
      </div>
      {have >= need && expectancy <= 0 && <span style={{ ...label, fontSize: 9, color: LOSS }}>Count reached, but expectancy needs to be positive</span>}
    </div>
  );
}

function Milestones({ strategy, r }: { strategy: string; r: StrategyReadiness }) {
  return (
    <Panel>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
        <span className="pal-chapter" style={{ fontFamily: TITLE_FONT, fontSize: 20, color: ICE, textShadow: HEAD_SHADOW }}>{strategy} milestones</span>
        <span style={{ ...label, color: r.ready ? WIN : TEAL }}>
          {r.ready ? 'Prescription generated · live trading unlocked' : r.paperUnlocked ? 'Paper unlocked · 30 positive paper trades generate your prescription' : 'Step 1 · 30 replay days with positive expectancy unlock paper trading'}
        </span>
      </div>
      <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
        <MilestoneBar name="Replay days" have={r.backtestDays} need={30} expectancy={r.backtestExpectancy} />
        <div style={{ flex: '1 1 260px', opacity: r.paperUnlocked ? 1 : 0.45 }}>
          <MilestoneBar name={r.paperUnlocked ? 'Paper trades' : 'Paper trades · locked'} have={r.paperTrades} need={30} expectancy={r.paperExpectancy} />
        </div>
      </div>
    </Panel>
  );
}

function LiveLock({ strategy, r, needsTier }: { strategy: string; r?: StrategyReadiness; needsTier: boolean }) {
  if (needsTier) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <LockIcon size={18} color={TEAL} />
          <span style={{ fontFamily: TITLE_FONT, fontSize: 20, color: ICE }}>Pick your {strategy} risk tier first</span>
        </span>
        <span style={{ ...label, lineHeight: 1.6 }}>{strategy}'s prescription is ready. Lock in a tier in the Prescription Engine and live logging opens.</span>
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <LockIcon size={18} color={TEAL} />
        <span style={{ fontFamily: TITLE_FONT, fontSize: 20, color: ICE }}>Live logging for {strategy} is locked</span>
      </span>
      <span style={{ ...label, lineHeight: 1.6 }}>
        It unlocks once {strategy} has a viable prescription: 30 replay days and 30 paper trades, both with positive expectancy. Log those in Readiness Progression.
      </span>
      {r && (
        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
          <MilestoneBar name="Replay days" have={r.backtestDays} need={30} expectancy={r.backtestExpectancy} />
          <MilestoneBar name="Paper trades" have={r.paperTrades} need={30} expectancy={r.paperExpectancy} />
        </div>
      )}
    </div>
  );
}

/** Bring trades over from the Inevitrade Notion Readiness Progression tracker (its Master Trade Log export). */
function ReadinessImportPanel({ username, onDone }: { username: string; onDone: () => Promise<unknown> }) {
  const input = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [over, setOver] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; lines: string[] } | null>(null);
  const run = async (upload: File) => {
    setBusy(true); setResult(null);
    try {
      const file = await csvFromUpload(upload);
      const r = await tradesApi.importReadiness(username, file);
      const modes = Object.entries(r.modes).map(([m, n]) => `${n} ${modeName(m as TradeMode)}`).join(', ');
      setResult({ ok: true, lines: [
        `Imported ${r.imported} trade${r.imported === 1 ? '' : 's'}${modes ? ` · ${modes}` : ''}`,
        r.duplicates ? `${r.duplicates} were already here (skipped)` : '',
        r.skipped.length ? `${r.skipped.length} rows skipped: no R/Factor or date (rows ${r.skipped.slice(0, 6).map(s => s.row).join(', ')}${r.skipped.length > 6 ? '…' : ''})` : '',
        r.newStrategies.length ? `New strategies added: ${r.newStrategies.join(', ')}` : '',
        r.fields.length ? `Your extra columns landed in stats: ${r.fields.map(f => f.replace(/_/g, ' ')).join(', ')}` : '',
        'Imported trades carry their R result (no prices), so they count toward your milestones, win rate and expectancy — not the reach curve.',
      ].filter(Boolean) });
      await onDone();
    } catch (err) {
      setResult({ ok: false, lines: [err instanceof Error ? err.message : 'Import failed'] });
    } finally {
      setBusy(false);
      if (input.current) input.current.value = '';
    }
  };
  const step = (n: number, text: ReactNode) => (
    <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
      <span style={{ flex: '0 0 22px', height: 22, borderRadius: 11, display: 'grid', placeItems: 'center', background: ACCENT, color: ON_ACCENT, fontFamily: PX_FONT, fontSize: 11, fontWeight: 800 }}>{n}</span>
      <span style={{ fontFamily: PX_FONT, fontSize: 13, lineHeight: 1.6, color: ICE }}>{text}</span>
    </div>
  );
  return (
    <div style={{ background: PANEL, boxShadow: pixelFrame(open), borderRadius: 'var(--pal-radius, 0)' }}>
      <button type="button" onClick={() => setOpen(o => !o)} aria-expanded={open}
        style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 14, padding: '14px 18px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', flexWrap: 'wrap' }}>
        <Upload size={16} color={ACCENT} />
        <span style={{ fontFamily: TITLE_FONT, fontWeight: 800, fontSize: 18, color: ICE }}>Bring over your Notion tracker</span>
        <span style={label}>Port your existing Readiness Progression trades in one drop</span>
        <span style={{ ...label, marginLeft: 'auto', color: ACCENT }}>{open ? '▴ Hide' : '▾ Import'}</span>
      </button>
      <div style={{ display: 'grid', gridTemplateRows: open ? '1fr' : '0fr', transition: 'grid-template-rows 0.4s cubic-bezier(0.22, 1, 0.36, 1)' }}>
        <div style={{ overflow: 'hidden', minHeight: 0 }}>
          <div style={{ padding: '0 18px 18px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 22 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {step(1, <>In Notion, open your <b>Readiness Progression</b> page and hover the <b>Master Trade Log</b> table.</>)}
              {step(2, <>Click <b>•••</b> → <b>Export</b> → format <b>Markdown &amp; CSV</b> → <b>Export</b>. Notion downloads a ZIP.</>)}
              {step(3, <>Drop that ZIP (or the CSV inside it) here. Replay, paper and live trades sort themselves; re-importing never duplicates.</>)}
            </div>
            <div
              tabIndex={0} role="button" aria-label="Drop your Notion export, or click to choose it"
              onClick={() => !busy && input.current?.click()}
              onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') input.current?.click(); }}
              onDragOver={e => { e.preventDefault(); setOver(true); }}
              onDragLeave={() => setOver(false)}
              onDrop={e => { e.preventDefault(); setOver(false); const f = e.dataTransfer.files[0]; if (f) run(f); }}
              style={{ minHeight: 150, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10, padding: 18, cursor: busy ? 'wait' : 'pointer',
                background: over ? 'var(--pal-tint-accent)' : FIELD, borderRadius: 'var(--pal-radius, 0)',
                boxShadow: `inset 0 0 0 1.5px ${over ? ACCENT : 'var(--pal-rim)'}`, outline: `2px dashed ${over ? ACCENT : 'transparent'}`, outlineOffset: -10 }}>
              <input ref={input} type="file" accept=".zip,.csv,text/csv,application/zip" hidden onChange={e => { const f = e.target.files?.[0]; if (f) run(f); }} />
              <Upload size={26} color={ACCENT} />
              <span style={{ fontFamily: PX_FONT, fontSize: 13, fontWeight: 700, color: ICE }}>{busy ? 'Importing…' : 'Drop the Notion export here'}</span>
              <span style={label}>ZIP or CSV · or click to choose</span>
            </div>
          </div>
          {result && (
            <div role="status" style={{ margin: '0 18px 18px', padding: '12px 14px', borderRadius: 'var(--pal-radius, 0)', background: FIELD, boxShadow: `inset 3px 0 0 ${result.ok ? WIN : LOSS}`, display: 'flex', flexDirection: 'column', gap: 4 }}>
              {result.lines.map((l, i) => <span key={i} style={{ fontFamily: PX_FONT, fontSize: 12, lineHeight: 1.5, color: i === 0 ? (result.ok ? WIN : LOSS) : TEAL }}>{l}</span>)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function LedgerCharts({ trades }: { trades: Trade[] }) {
  // Oldest first, so curves and streaks read left to right in time
  const series: ChartTrade[] = trades.slice().reverse().map(t => ({ r: Number(t.r_multiple), mr: t.max_run_r === null ? null : Math.max(0, Number(t.max_run_r)) }));
  if (!series.length) return null;
  return (
    <>
      <ChartCard title="Reach curve" hint="Share of trades that ran at least this far. Each marker is an exit level: how often price reached it at all.">
        <ReachCurve trades={series} />
      </ChartCard>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(420px, 1fr))', gap: 24 }}>
        <ChartCard title="Equity curve" hint="Cumulative R, trade by trade. Red = drawdown from the peak."><EquityCurve trades={series} /></ChartCard>
        <ChartCard title="Outcomes" hint="How many trades landed in each half-R bucket."><OutcomeHistogram trades={series} /></ChartCard>
        <ChartCard title="Streaks" hint="Your recent run of wins and losses."><Streaks trades={series} /></ChartCard>
      </div>
    </>
  );
}

const IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
const MAX_IMAGE_BYTES = 5 * 1024 * 1024;

/* Paste (⌘V anywhere on the page), drop, or pick a chart screenshot */
function ScreenshotDrop({ image, onChange }: { image: File | null; onChange: (f: File | null) => void }) {
  const [preview, setPreview] = useState<string | null>(null);
  const [hint, setHint] = useState<string | null>(null);
  const [over, setOver] = useState(false);
  const [focused, setFocused] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  const accept = useCallback((f: File | null | undefined) => {
    if (!f) return;
    if (!IMAGE_TYPES.includes(f.type)) { setHint('PNG, JPEG or WebP only'); return; }
    if (f.size > MAX_IMAGE_BYTES) { setHint('Image must be under 5MB'); return; }
    setHint(null);
    onChange(f);
  }, [onChange]);

  useEffect(() => {
    const onPaste = (e: ClipboardEvent) => {
      const item = [...(e.clipboardData?.items ?? [])].find(i => i.type.startsWith('image/'));
      if (item) { e.preventDefault(); accept(item.getAsFile()); }
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, [accept]);

  useEffect(() => {
    if (!image) { setPreview(null); return; }
    const url = URL.createObjectURL(image);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [image]);

  return (
    <div>
      <div style={{ ...label, marginBottom: 6 }}>Screenshot</div>
      {/* Clicking the box just selects it as the paste target (no file dialog); Browse opens files */}
      <div
        tabIndex={0}
        role="group"
        aria-label="Screenshot: paste or drop an image"
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onDragOver={e => { e.preventDefault(); setOver(true); }}
        onDragLeave={() => setOver(false)}
        onDrop={e => { e.preventDefault(); setOver(false); accept(e.dataTransfer.files[0]); }}
        style={{
          display: 'flex', alignItems: 'center', gap: 14, padding: 12, cursor: 'text', minHeight: 72,
          background: over || focused ? 'var(--pal-tint-accent)' : FIELD,
          boxShadow: `0 0 0 ${focused ? 1.5 : 2}px ${over || focused ? ACCENT : RIM}`,
          outline: `2px dashed ${over ? TEAL : 'transparent'}`, outlineOffset: -8,
        }}
      >
        <input ref={fileInput} type="file" accept={IMAGE_TYPES.join(',')} style={{ display: 'none' }} onChange={e => { accept(e.target.files?.[0]); e.target.value = ''; }} />
        {preview ? (
          <>
            <img src={preview} alt="Trade screenshot preview" style={{ height: 64, maxWidth: 160, objectFit: 'cover', boxShadow: `0 0 0 2px ${INK}` }} />
            <span style={{ ...label, color: ICE }}>{image?.name || 'Pasted image'}</span>
            <button type="button" onClick={e => { e.preventDefault(); onChange(null); }} style={{ ...label, marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: LOSS }}>Remove</button>
          </>
        ) : (
          <>
            <ImagePlus size={22} color={focused ? ACCENT : TEAL} />
            <span style={{ ...label, lineHeight: 1.6, color: focused ? ACCENT : TEAL }}>
              {focused ? 'Ready — press ⌘V to paste your chart' : 'Click here and paste (⌘V), or drop an image'}
            </span>
            <button type="button" onClick={() => fileInput.current?.click()}
              style={{ ...label, marginLeft: 'auto', padding: '6px 12px', border: 'none', cursor: 'pointer', background: PANEL, color: ICE, boxShadow: `0 0 0 1px ${RIM}`, borderRadius: 'var(--pal-radius, 0)' }}>
              Browse…
            </button>
          </>
        )}
      </div>
      {hint && <div style={{ ...label, color: LOSS, marginTop: 6 }}>{hint}</div>}
    </div>
  );
}

function Lightbox({ src, onClose }: { src: string; onClose: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div role="dialog" aria-label="Trade screenshot" onClick={onClose}
      style={{ position: 'fixed', inset: 0, zIndex: 100, background: 'rgba(2,10,16,0.88)', display: 'grid', placeItems: 'center', padding: 24, cursor: 'zoom-out' }}>
      <img src={src} alt="Trade screenshot" style={{ maxWidth: '100%', maxHeight: '100%', boxShadow: pixelFrame(true) }} />
    </div>
  );
}

function Field({ name, children }: { name: string; children: ReactNode }) {
  return <label style={{ display: 'block' }}><div style={{ ...label, marginBottom: 6 }}>{name}</div>{children}</label>;
}

interface StatFieldsState {
  fields: StatField[];
  catalog: StatFieldDef[];
  addCatalog: (key: string) => Promise<void>;
  addCustom: (f: { label: string; type: StatFieldType; options: string[] }) => Promise<void>;
  remove: (id: string) => Promise<void>;
}

function useStatFields(username: string): StatFieldsState {
  const [fields, setFields] = useState<StatField[]>([]);
  const [catalog, setCatalog] = useState<StatFieldDef[]>([]);
  const load = useCallback(async () => {
    const r = await statFieldsApi.list(username);
    setFields(r.fields);
    setCatalog(r.catalog);
  }, [username]);
  useEffect(() => { load().catch(() => undefined); }, [load]);
  return {
    fields,
    catalog,
    addCatalog: async key => { await statFieldsApi.addCatalog(username, key); await load(); },
    addCustom: async f => { await statFieldsApi.addCustom(username, f); await load(); },
    remove: async id => { await statFieldsApi.remove(username, id); await load(); },
  };
}

const TYPE_NAMES: Record<StatFieldType, string> = {
  yesno: 'Yes / no', choice: 'Pick one', multi: 'Pick several', number: 'Number', price: 'Price', text: 'Text',
};

function StatInput({ f, value, onChange }: { f: StatFieldDef; value: unknown; onChange: (v: unknown) => void }) {
  if (f.type === 'yesno') {
    return <Segmented value={value === true ? 'yes' : value === false ? 'no' : ''} options={['yes', 'no']} onChange={v => onChange(v === 'yes')} />;
  }
  if (f.type === 'choice') {
    return (
      <select style={inputStyle} value={String(value ?? '')} onChange={ev => onChange(ev.target.value || undefined)}>
        <option value="">—</option>
        {f.options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  }
  if (f.type === 'multi') {
    const picks = Array.isArray(value) ? (value as string[]) : [];
    const extras = picks.filter(p => !f.options.includes(p));
    return (
      <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', alignItems: 'center' }}>
        {[...f.options, ...extras].map(o => {
          const on = picks.includes(o);
          return (
            <button key={o} type="button" onClick={() => onChange(on ? picks.filter(x => x !== o) : [...picks, o])}
              style={{ fontFamily: PX_FONT, fontSize: 9, letterSpacing: '0.08em', padding: '5px 7px', border: 'none', cursor: 'pointer', background: on ? ACCENT : FIELD, color: on ? ON_ACCENT : TEAL, boxShadow: `0 0 0 2px ${on ? INK : RIM}` }}>
              {o}
            </button>
          );
        })}
        {f.allowCustom && (
          <input style={{ ...inputStyle, width: 110, padding: '5px 7px', fontSize: 10 }} placeholder="+ other"
            onKeyDown={ev => {
              const v = ev.currentTarget.value.trim();
              if (ev.key === 'Enter' && v) { ev.preventDefault(); if (!picks.includes(v)) onChange([...picks, v]); ev.currentTarget.value = ''; }
            }} />
        )}
      </div>
    );
  }
  const numeric = f.type === 'number' || f.type === 'price';
  return <input style={inputStyle} inputMode={numeric ? 'decimal' : undefined} value={String(value ?? '')} onChange={ev => onChange(ev.target.value)} />;
}

/* The trader's chosen stats, laid out in the same rows as the prices, plus the Add stat picker */
function StatFieldsRow({ stats, values, onChange }: { stats: StatFieldsState; values: Record<string, unknown>; onChange: (key: string, v: unknown) => void }) {
  const [picking, setPicking] = useState(false);
  const [custom, setCustom] = useState(false);
  const [name, setName] = useState('');
  const [type, setType] = useState<StatFieldType>('yesno');
  const [opts, setOpts] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const have = new Set(stats.fields.map(f => f.key));
  const available = stats.catalog.filter(c => !have.has(c.key));

  const addCustom = async () => {
    setErr(null);
    try {
      await stats.addCustom({ label: name, type, options: opts.split(',').map(o => o.trim()).filter(Boolean) });
      setName(''); setOpts(''); setCustom(false); setPicking(false);
    } catch (e) { setErr(e instanceof Error ? e.message : 'Could not add stat'); }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {stats.fields.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 14, alignItems: 'end' }}>
          {stats.fields.filter(f => !f.parent || values[f.parent] === true).map(f => (
            <div key={f.id} style={f.type === 'multi' ? { gridColumn: 'span 2' } : undefined}>
              <div style={{ ...label, marginBottom: 6, display: 'flex', justifyContent: 'space-between', gap: 6 }}>
                <span>{f.label}</span>
                {!f.parent && (
                  <button type="button" aria-label={`Remove ${f.label}`} title="Remove from form"
                    onClick={() => stats.remove(f.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: TEAL, padding: 0, fontSize: 10 }}>✕</button>
                )}
              </div>
              <StatInput f={f} value={values[f.key]} onChange={v => onChange(f.key, v)} />
            </div>
          ))}
        </div>
      )}
      <div style={{ position: 'relative', alignSelf: 'flex-start' }}>
        <button type="button" onClick={() => { setPicking(p => !p); setCustom(false); }}
          style={{ ...label, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>+ Add stat</button>
        {picking && (
          <div style={{ position: 'absolute', zIndex: 20, top: 22, left: 0, width: 300, background: PANEL, padding: 12, boxShadow: pixelFrame(true), display: 'flex', flexDirection: 'column', gap: 4 }}>
            {!custom && available.map(c => (
              <button key={c.key} type="button" onClick={async () => { await stats.addCatalog(c.key); setPicking(false); }}
                style={{ ...label, color: ICE, textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', padding: '6px 4px', display: 'flex', justifyContent: 'space-between' }}>
                <span>{c.label}</span><span style={{ color: TEAL, fontSize: 8 }}>{TYPE_NAMES[c.type]}</span>
              </button>
            ))}
            {!custom && !available.length && <span style={{ ...label, fontSize: 9, padding: 4 }}>You've added every built-in stat.</span>}
            {!custom ? (
              <button type="button" onClick={() => setCustom(true)}
                style={{ ...label, color: GOLD, textAlign: 'left', background: 'none', border: 'none', borderTop: `1px solid ${RIM}`, cursor: 'pointer', padding: '8px 4px 2px' }}>
                ✎ Custom stat…
              </button>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <input style={inputStyle} placeholder="Name, e.g. Pre-market plan" value={name} onChange={e => setName(e.target.value)} maxLength={40} autoFocus />
                <select style={inputStyle} value={type} onChange={e => setType(e.target.value as StatFieldType)}>
                  {(Object.keys(TYPE_NAMES) as StatFieldType[]).map(t => <option key={t} value={t}>{TYPE_NAMES[t]}</option>)}
                </select>
                {(type === 'choice' || type === 'multi') && (
                  <input style={inputStyle} placeholder="Options, comma separated" value={opts} onChange={e => setOpts(e.target.value)} />
                )}
                {err && <span style={{ ...label, color: LOSS }}>{err}</span>}
                <div style={{ display: 'flex', gap: 10 }}>
                  <button type="button" onClick={addCustom} style={{ ...label, color: ON_ACCENT, background: ACCENT, border: 'none', padding: '6px 10px', cursor: 'pointer' }}>Add</button>
                  <button type="button" onClick={() => setCustom(false)} style={{ ...label, background: 'none', border: 'none', cursor: 'pointer' }}>Back</button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function LogTradeForm({ strategy, defaultMode, modes = MODES, onSubmit, stats, chartReader }: {
  strategy: TradeStrategy; defaultMode: TradeMode; modes?: TradeMode[]; onSubmit: (t: NewTrade, image: File | null) => Promise<void>;
  stats: StatFieldsState;
  /** Inner Circle only; null hides the Read chart button */
  chartReader: { enabled: boolean; read: (image: File) => Promise<ChartDraft> } | null;
}) {
  const [reading, setReading] = useState(false);
  const [draftNote, setDraftNote] = useState<{ tone: string; text: string } | null>(null);
  const [mode, setMode] = useState<TradeMode>(defaultMode);
  useEffect(() => setMode(defaultMode), [defaultMode]);
  const [pair, setPair] = useState('MNQ');
  const [tradedOn, setTradedOn] = useState(today);
  const [session, setSession] = useState('NY AM');
  const [timeframe, setTimeframe] = useState('');
  const [entry, setEntry] = useState('');
  const [stop, setStop] = useState('');
  const [maxRun, setMaxRun] = useState('');
  const [exit, setExit] = useState('');
  /** Exit shortcuts: stopped out (exit = stop) or break even (exit = entry) */
  const [exitAt, setExitAt] = useState<'stop' | 'be' | null>(null);
  const exitValue = exitAt === 'stop' ? stop : exitAt === 'be' ? entry : exit;
  const [quantity, setQuantity] = useState('');
  const [heat, setHeat] = useState('');
  const [partials, setPartials] = useState<{ price: string; qty: string }[]>([]);
  const [context, setContext] = useState<Record<string, unknown>>({});
  const [partialOn, setPartialOn] = useState(false);
  const [followed, setFollowed] = useState(true);
  const [notes, setNotes] = useState('');
  const [image, setImage] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Live read-out of what the four prices mean, before saving
  const e = Number(entry), st = Number(stop), mr = Number(maxRun), ex = Number(exitValue);
  const priced = [entry, stop, maxRun, exitValue].every(v => v.trim() !== '') && [e, st, mr, ex].every(Number.isFinite) && e !== st;
  const dir = st < e ? 1 : -1;
  const oneR = Math.abs(e - st);
  const pv = POINT_VALUES[pair] ?? 1;
  const legs = (partialOn ? partials : []).filter(p => p.price.trim() !== '' && p.qty.trim() !== '').map(p => ({ price: Number(p.price), qty: Number(p.qty) }));
  const qty = Number(quantity);
  const legQty = legs.reduce((sum, p) => sum + p.qty, 0);
  const rAt = (price: number) => (dir * (price - e)) / oneR;
  const blended = legs.length && qty > 0
    ? legs.reduce((sum, p) => sum + (p.qty / qty) * rAt(p.price), 0) + ((qty - legQty) / qty) * rAt(ex)
    : rAt(ex);
  const preview = priced ? {
    side: dir === 1 ? 'Long' : 'Short',
    oneR,
    result: blended,
    run: (dir * (mr - e)) / oneR,
    heat: heat.trim() && Number.isFinite(Number(heat)) ? (dir * (e - Number(heat))) / oneR : null,
    dollars: quantity
      ? (legs.reduce((sum, p) => sum + dir * (p.price - e) * p.qty, 0) + dir * (ex - e) * (qty - legQty)) * pv
      : null,
  } : null;

  const applyDraft = (d: ChartDraft) => {
    const str = (n: number | null) => (n === null || n === undefined ? '' : String(n));
    if (d.pair) setPair(d.pair.toUpperCase());
    if (d.traded_on) setTradedOn(d.traded_on);
    if (d.session && SESSIONS.includes(d.session)) setSession(d.session);
    if (d.timeframe) setTimeframe(d.timeframe);
    setEntry(str(d.entry_price)); setStop(str(d.stop_price)); setMaxRun(str(d.max_run_price)); setExit(str(d.exit_price)); setExitAt(null);
    setHeat(str(d.max_heat_price ?? null));
    if (d.partials.length) {
      setPartialOn(true);
      setPartials(d.partials.map(p => ({ price: String(p.price), qty: p.qty === null ? '' : String(p.qty) })));
    }
    if (d.sweep && stats.fields.some(f => f.key === 'sweep')) setContext(c => ({ ...c, sweep: d.sweep }));
    const gaps = d.uncertain.length ? ` Check: ${d.uncertain.join('; ')}.` : '';
    setDraftNote({
      tone: d.confidence === 'high' ? WIN : d.confidence === 'medium' ? GOLD : LOSS,
      text: `Filled from your chart (${d.confidence} confidence) — review before saving.${gaps}${d.partials.some(p => p.qty === null) || (d.partials.length && !quantity) ? ' Add contract counts for the partials.' : ''}`,
    });
  };

  const readChart = async () => {
    if (!image || !chartReader) return;
    setReading(true);
    setDraftNote(null);
    try { applyDraft(await chartReader.read(image)); }
    catch (err) { setDraftNote({ tone: LOSS, text: err instanceof Error ? err.message : 'Could not read the chart' }); }
    finally { setReading(false); }
  };

  const reset = () => {
    setEntry(''); setStop(''); setMaxRun(''); setExit(''); setExitAt(null); setHeat(''); setPartials([]);
    setContext({}); setDraftNote(null); setPartialOn(false); setNotes(''); setFollowed(true); setImage(null);
  };

  const submit = async (ev: FormEvent) => {
    ev.preventDefault();
    if (!priced) {
      setError('Enter entry, stop, max run and exit prices');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await onSubmit({
        mode, strategy, pair, traded_on: tradedOn, session, timeframe: timeframe || null,
        entry_price: e, stop_price: st, max_run_price: mr, exit_price: ex,
        quantity: quantity ? Number(quantity) : null,
        max_heat_price: heat.trim() ? Number(heat) : null,
        partials: partialOn ? legs : [],
        context,
        followed_rules: followed, notes: notes.trim() || undefined,
      }, image);
      reset();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save trade');
    } finally {
      setBusy(false);
    }
  };

  const grid2: CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 };
  const rowGrid: CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 14, alignItems: 'end' };

  return (
    <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div><span className="pal-chapter" style={{ fontFamily: TITLE_FONT, fontSize: 22, color: ICE, textShadow: HEAD_SHADOW }}>Log a {strategy} trade</span></div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 14 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <ScreenshotDrop image={image} onChange={setImage} />
          {chartReader && image && (
            <button type="button" onClick={readChart} disabled={reading || !chartReader.enabled}
              title={chartReader.enabled ? 'Draft the trade from this screenshot' : 'Needs an Anthropic API key on the server'}
              style={{ ...label, alignSelf: 'flex-start', padding: '7px 11px', border: 'none', cursor: reading || !chartReader.enabled ? 'not-allowed' : 'pointer',
                color: chartReader.enabled ? INK : TEAL, background: chartReader.enabled ? GOLD : FIELD, boxShadow: `0 0 0 2px ${INK}`, opacity: reading ? 0.7 : 1 }}>
              {reading ? 'Reading chart…' : chartReader.enabled ? '✦ Read chart' : '✦ Read chart · needs API key'}
            </button>
          )}
          {draftNote && <span style={{ ...label, fontSize: 9, lineHeight: 1.6, color: draftNote.tone }}>{draftNote.text}</span>}
        </div>
        <Field name="Notes"><textarea style={{ ...inputStyle, minHeight: 72, resize: 'vertical' }} value={notes} onChange={ev => setNotes(ev.target.value)} maxLength={2000} /></Field>
      </div>
      <div style={rowGrid}>
        {modes.length > 1 && <div><div style={{ ...label, marginBottom: 6 }}>Mode</div><Segmented value={mode} options={modes} onChange={setMode} format={modeName} /></div>}
        <Field name="Date"><input style={inputStyle} type="date" value={tradedOn} onChange={ev => setTradedOn(ev.target.value)} required /></Field>
        <div><div style={{ ...label, marginBottom: 6 }}>Session</div><Segmented value={session} options={SESSIONS} onChange={setSession} /></div>
      </div>
      <div style={rowGrid}>
        <Field name="Pair"><input style={inputStyle} placeholder="MNQ, BTCUSDT…" value={pair} onChange={ev => setPair(ev.target.value.toUpperCase())} maxLength={20} /></Field>
        <Field name="Timeframe"><input style={inputStyle} placeholder="15s, 1m…" value={timeframe} onChange={ev => setTimeframe(ev.target.value)} /></Field>
        <Field name="Contracts / units"><input style={inputStyle} inputMode="decimal" placeholder="optional" value={quantity} onChange={ev => setQuantity(ev.target.value)} /></Field>
        {/* Partial: tick it, then the first partial's price (and qty) go right here; more partials below */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <span style={label}>Partial taken</span>
            <input type="checkbox" aria-label="Partial taken" checked={partialOn} style={{ accentColor: ACCENT, margin: 0 }}
              onChange={ev => { setPartialOn(ev.target.checked); if (ev.target.checked && !partials.length) setPartials([{ price: '', qty: '' }]); }} />
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <input style={{ ...inputStyle, flex: 2, minWidth: 0, opacity: partialOn ? 1 : 0.55 }} inputMode="decimal" aria-label="Partial price"
              placeholder={partialOn ? 'price' : 'optional'} disabled={!partialOn} value={partials[0]?.price ?? ''}
              onChange={ev => setPartials(xs => xs.map((x, j) => (j === 0 ? { ...x, price: ev.target.value } : x)))} />
            {partialOn && (
              <input style={{ ...inputStyle, flex: 1, minWidth: 0 }} inputMode="decimal" aria-label="Partial quantity" placeholder="qty" value={partials[0]?.qty ?? ''}
                onChange={ev => setPartials(xs => xs.map((x, j) => (j === 0 ? { ...x, qty: ev.target.value } : x)))} />
            )}
          </div>
        </div>
      </div>

      <div style={{ ...label, color: GOLD, marginTop: 4 }}>The prices</div>
      <div style={rowGrid}>
        <Field name="Entry"><input style={inputStyle} inputMode="decimal" value={entry} onChange={ev => setEntry(ev.target.value)} /></Field>
        <Field name="Stop"><input style={inputStyle} inputMode="decimal" value={stop} onChange={ev => setStop(ev.target.value)} /></Field>
        {/* Order follows the trade: where it closed, how far it went against you, how far it ran */}
        {/* Exit shortcuts sit beside the label so the input lines up with its neighbours */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <span style={label}>Exit</span>
            <span style={{ display: 'flex', gap: 10 }}>
              {([['stop', 'Stop'], ['be', 'BE']] as const).map(([k, name]) => (
                <label key={k} title={k === 'stop' ? 'Stopped out: exit at the stop' : 'Break even: exit at the entry'}
                  style={{ ...label, fontSize: 9, display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', textTransform: 'none', letterSpacing: '0.06em' }}>
                  <input type="checkbox" checked={exitAt === k} onChange={ev => setExitAt(ev.target.checked ? k : null)} style={{ accentColor: ACCENT, margin: 0 }} />
                  {name}
                </label>
              ))}
            </span>
          </div>
          <input style={{ ...inputStyle, opacity: exitAt ? 0.6 : 1 }} inputMode="decimal" aria-label="Exit"
            value={exitAt === 'stop' ? stop : exitAt === 'be' ? entry : exit} readOnly={exitAt !== null}
            onChange={ev => setExit(ev.target.value)} />
        </div>
        <Field name="Max adverse (optional)"><input style={inputStyle} inputMode="decimal" value={heat} onChange={ev => setHeat(ev.target.value)} /></Field>
        <Field name="Max run"><input style={inputStyle} inputMode="decimal" value={maxRun} onChange={ev => setMaxRun(ev.target.value)} /></Field>
      </div>

      <StatFieldsRow stats={stats} values={context} onChange={(k, v) => setContext(c => ({ ...c, [k]: v }))} />

      {preview && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, padding: '10px 12px', background: FIELD, boxShadow: `0 0 0 2px ${RIM}` }}>
          <span style={{ ...label, color: ICE }}>{preview.side}</span>
          <span style={label}>1R = {preview.oneR.toFixed(2)} pts</span>
          <span style={label}>range {((preview.oneR / e) * 100).toFixed(3)}%</span>
          <span style={{ ...label, color: preview.result > 0 ? WIN : LOSS }}>{r(preview.result)}</span>
          <span style={label}>ran {r(preview.run)}</span>
          {preview.heat !== null && <span style={{ ...label, color: GOLD }}>adverse {preview.heat.toFixed(2)}R</span>}
          {preview.dollars !== null && <span style={{ ...label, color: preview.dollars >= 0 ? WIN : LOSS }}>${preview.dollars.toFixed(2)}</span>}
        </div>
      )}

      <div style={{ display: 'flex', gap: 28, flexWrap: 'wrap' }}>
        {/* No prescription exists yet during replay/paper, so adherence is a live-trading question */}
        {!modes.every(m => m !== 'live') && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
            <input type="checkbox" checked={followed} onChange={ev => setFollowed(ev.target.checked)} style={{ width: 16, height: 16, accentColor: ACCENT }} />
            <span style={{ ...label, color: ICE }}>Followed my prescription</span>
          </label>
        )}
      </div>
      {partialOn && (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {partials.map((p, i) => i === 0 ? null : (
          <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'end', flexWrap: 'wrap' }}>
            <Field name={`Partial ${i + 1} price`}>
              <input style={{ ...inputStyle, width: 150 }} inputMode="decimal" value={p.price}
                onChange={ev => setPartials(xs => xs.map((x, j) => (j === i ? { ...x, price: ev.target.value } : x)))} />
            </Field>
            <Field name="Qty closed">
              <input style={{ ...inputStyle, width: 110 }} inputMode="decimal" placeholder="2" value={p.qty}
                onChange={ev => setPartials(xs => xs.map((x, j) => (j === i ? { ...x, qty: ev.target.value } : x)))} />
            </Field>
            <button type="button" onClick={() => setPartials(xs => xs.filter((_, j) => j !== i))}
              style={{ ...label, background: 'none', border: 'none', cursor: 'pointer', color: LOSS, paddingBottom: 10 }}>Remove</button>
          </div>
        ))}
        <button type="button" onClick={() => setPartials(xs => [...xs, { price: '', qty: '' }])}
          style={{ ...label, alignSelf: 'flex-start', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
          + Add another partial
        </button>
        {partials.length > 0 && !quantity && <span style={{ ...label, fontSize: 9, color: GOLD }}>Enter total contracts / units so the partials can be weighed</span>}
      </div>
      )}
      {error && <div style={{ ...label, color: LOSS }}>{error}</div>}
      <button
        type="submit"
        disabled={busy}
        style={{
          alignSelf: 'flex-start', minWidth: 220,
          padding: '12px 16px', border: 'none', cursor: busy ? 'wait' : 'pointer',
          background: !preview || Math.abs(preview.result) < 0.005 ? ACCENT : preview.result > 0 ? WIN : LOSS,
          color: ON_ACCENT, fontFamily: PX_FONT, fontSize: 12, letterSpacing: '0.16em', textTransform: CASE, transition: 'background 0.25s',
          boxShadow: `0 0 0 2px ${INK}, inset 2px 2px 0 rgba(255,255,255,0.4), inset -2px -2px 0 rgba(0,0,0,0.25)`,
          filter: `drop-shadow(0 3px 0 ${INK})`, opacity: busy ? 0.7 : 1,
        }}
      >
        {busy ? 'Saving…' : preview && Number.isFinite(preview.result) ? `Log trade · ${r(preview.result)}` : 'Log trade'}
      </button>
    </form>
  );
}

function TradeTable({ trades, onDelete, onView }: { trades: Trade[]; onDelete: (t: Trade) => void; onView: (t: Trade) => void }) {
  if (!trades.length) {
    return <div style={{ ...label, padding: '28px 0', textAlign: 'center' }}>No trades yet. Log your first one.</div>;
  }
  const th: CSSProperties = { ...label, textAlign: 'left', padding: '8px 10px', borderBottom: `2px solid ${RIM}`, whiteSpace: 'nowrap' };
  const td: CSSProperties = { fontFamily: PX_FONT, fontSize: 11, color: ICE, padding: '9px 10px', borderBottom: `1px solid ${RIM}`, whiteSpace: 'nowrap' };
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            {['Date', 'Mode', 'Pair', 'Side', 'Range', 'Result', 'Ran', 'Heat', '$', 'Rules', 'Chart', 'Notes', ''].map(h => <th key={h} style={th}>{h}</th>)}
          </tr>
        </thead>
        <tbody>
          {trades.map(t => {
            const rv = Number(t.r_multiple);
            return (
              <tr key={t.id}>
                <td style={td}>{new Date(t.traded_on).toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' })}{t.session ? ` · ${t.session}` : ''}</td>
                <td style={{ ...td, color: TEAL, textTransform: CASE }}>{t.mode}</td>
                <td style={td}>{t.pair}</td>
                <td style={{ ...td, textTransform: CASE }}>{t.direction}</td>
                <td style={{ ...td, color: TEAL }}>{t.entry_price === null ? (typeof t.context?.range_pct === 'number' ? `${t.context.range_pct}%` : '—') : `${((Math.abs(Number(t.entry_price) - Number(t.stop_price)) / Number(t.entry_price)) * 100).toFixed(3)}%`}</td>
                <td style={{ ...td, color: rv > 0 ? WIN : LOSS }}>{r(rv)}{t.partials?.length ? <span title={t.partials.map(p => `${p.qty} @ ${p.price}`).join(', ')} style={{ marginLeft: 6, padding: '1px 4px', fontSize: 9, color: INK, background: GOLD }}>P</span> : null}</td>
                <td style={{ ...td, color: TEAL }} title={t.max_run_r === null ? 'Imported without prices' : undefined}>{t.max_run_r === null ? '—' : r(Number(t.max_run_r))}</td>
                <td style={{ ...td, color: GOLD }}>{t.max_heat_r === null ? '—' : `${Number(t.max_heat_r).toFixed(2)}R`}</td>
                <td style={{ ...td, color: t.pnl_dollars === null ? TEAL : t.pnl_dollars >= 0 ? WIN : LOSS }}>{t.pnl_dollars === null ? '—' : `$${t.pnl_dollars.toLocaleString()}`}</td>
                <td style={td}>{t.followed_rules ? <Check size={14} color={WIN} /> : <X size={14} color={LOSS} />}</td>
                <td style={td}>
                  {t.has_image && (
                    <button type="button" onClick={() => onView(t)} aria-label="View screenshot" style={{ background: 'none', border: 'none', cursor: 'zoom-in', color: TEAL, padding: 2 }}>
                      <ImageIcon size={15} />
                    </button>
                  )}
                </td>
                <td style={{ ...td, whiteSpace: 'normal', maxWidth: 220, color: SOFT_TEXT }}>{t.notes ?? ''}</td>
                <td style={td}>
                  <button type="button" onClick={() => onDelete(t)} aria-label="Delete trade" style={{ background: 'none', border: 'none', cursor: 'pointer', color: TEAL, padding: 2 }}>
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/* "+" tab: define a strategy beyond the built-ins */
function NewStrategy({ onCreate, onClose }: { onCreate: (s: { name: string; code: string }) => Promise<void>; onClose: () => void }) {
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setErr(null);
    try { await onCreate({ name, code }); }
    catch (x) { setErr(x instanceof Error ? x.message : 'Could not create strategy'); }
  };
  return (
    <Panel>
      <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ fontFamily: TITLE_FONT, fontSize: 22, color: ICE, textShadow: HEAD_SHADOW }}>New strategy</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14, alignItems: 'end' }}>
          <Field name="Name"><input style={inputStyle} placeholder="Opening range breakout" value={name} onChange={e => setName(e.target.value)} maxLength={40} required autoFocus /></Field>
          <Field name="Code (tab label)"><input style={inputStyle} placeholder="ORB" value={code} onChange={e => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))} maxLength={8} required /></Field>
        </div>
        <div style={{ ...label, fontSize: 9, lineHeight: 1.6 }}>
          Until you've logged 20 trades (backtests count), Marathon sizing assumes a 9-loss streak to stay safe. After that it uses your real worst streak.
        </div>
        {err && <div style={{ ...label, color: LOSS }}>{err}</div>}
        <div style={{ display: 'flex', gap: 10 }}>
          <button type="submit" style={{ ...label, color: ON_ACCENT, background: ACCENT, border: 'none', padding: '8px 14px', cursor: 'pointer', boxShadow: `0 0 0 2px ${INK}` }}>Create</button>
          <button type="button" onClick={onClose} style={{ ...label, background: 'none', border: 'none', cursor: 'pointer' }}>Cancel</button>
        </div>
      </form>
    </Panel>
  );
}

/** The trade ledger. `embedded` drops the page chrome so Home Base can slide it open in place. */
export function Tracker({ embedded = false, onViewChange, track = 'all' }: { embedded?: boolean; onViewChange?: (v: 'log' | 'stats') => void; track?: TrackerTrack } = {}) {
  const modes = TRACK_MODES[track];
  const navigate = useNavigate();
  const copy = useSkinCopy();
  const { user } = useAppState();
  const username = user?.discordUsername || 'trader';
  const [strategy, setStrategy] = useState<TradeStrategy>('VCL');
  const [strategies, setStrategies] = useState<StrategyDef[]>([]);
  const [creatingStrategy, setCreatingStrategy] = useState(false);
  const loadStrategies = useCallback(() => strategiesApi.list(username).then(setStrategies).catch(() => undefined), [username]);
  useEffect(() => { loadStrategies(); }, [loadStrategies]);
  const [mode, setMode] = useState<TradeMode>(modes[0]);
  const [readiness, setReadiness] = useState<Record<string, StrategyReadiness> | null>(null);
  const loadReadiness = useCallback(() => tradesApi.readiness(username).then(r => setReadiness(r.strategies)).catch(() => setReadiness(null)), [username]);
  useEffect(() => { if (track !== 'all') loadReadiness(); }, [track, loadReadiness]);
  // Live opens per strategy: readiness complete, then a risk tier locked in the Prescription Engine
  const [tiered, setTiered] = useState<string[] | null>(null);
  useEffect(() => { if (track === 'live') engineApi.choices(username).then(c => setTiered(c.map(x => x.strategy))).catch(() => setTiered(null)); }, [track, username]);
  const needsTier = track === 'live' && Boolean(readiness?.[strategy]?.ready) && !readiness?.[strategy]?.fastTrack && tiered !== null && !tiered.includes(strategy);
  const liveLocked = track === 'live' && readiness !== null && (!readiness[strategy]?.ready || needsTier);
  // Paper unlocks after the replay milestone; until then the readiness form only logs replay trades
  const formModes = track === 'readiness' && readiness && !readiness[strategy]?.paperUnlocked ? (['backtest'] as TradeMode[]) : modes;
  const [trades, setTrades] = useState<Trade[]>([]);
  const [summary, setSummary] = useState<TradeSummary | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [viewing, setViewing] = useState<string | null>(null);
  const [savedNote, setSavedNote] = useState<string | null>(null);
  useEffect(() => { if (!savedNote) return; const t = setTimeout(() => setSavedNote(null), 5000); return () => clearTimeout(t); }, [savedNote]);
  const [view, setView] = useState<'log' | 'stats'>(() => (new URLSearchParams(window.location.search).get('view') === 'stats' ? 'stats' : 'log'));
  useEffect(() => { onViewChange?.(view); }, [view, onViewChange]);
  const [marathonOpen, setMarathonOpen] = useState(false);
  const [statsOpen, setStatsOpen] = useState(false);
  const [editingRoutine, setEditingRoutine] = useState(false);
  const [routineVersion, setRoutineVersion] = useState(0);
  const [marathonLockedNote, setMarathonLockedNote] = useState(false);
  const [roles, setRoles] = useState<string[]>([]);
  useEffect(() => { authApi.me(username).then(me => setRoles(me.roles)).catch(() => setRoles([])); }, [username]);
  const innerCircle = roles.includes('inner_circle');
  const stats = useStatFields(username);
  const [readerEnabled, setReaderEnabled] = useState(false);
  useEffect(() => {
    if (!innerCircle) return;
    tradesApi.readChartStatus(username).then(r => setReaderEnabled(r.enabled)).catch(() => setReaderEnabled(false));
  }, [innerCircle, username]);

  const refresh = useCallback(async () => {
    try {
      const [list, stats] = await Promise.all([
        tradesApi.list(username, { strategy, mode, limit: 1000 }),
        tradesApi.stats(username, { strategy, mode }),
      ]);
      setTrades(list);
      setSummary(stats);
      setLoadError(null);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Could not reach the ledger');
    }
  }, [username, strategy, mode]);

  useEffect(() => { refresh(); }, [refresh]);

  const tab = (on: boolean): CSSProperties => ({
    fontFamily: TITLE_FONT, fontSize: 22, letterSpacing: '0.08em', padding: '10px 22px', border: 'none', cursor: 'pointer',
    background: on ? TAB_ON : TAB_OFF, color: on ? 'var(--pal-tab-on-text)' : TEAL,
    boxShadow: on ? `0 0 0 2px ${TEAL}, 0 0 0 4px ${INK}, 0 0 22px rgba(61,217,200,0.25)` : `0 0 0 2px ${RIM}, 0 0 0 4px ${INK}`,
    textShadow: on ? HEAD_SHADOW : 'none',
  });

  return (
    <div style={embedded ? { position: 'relative' } : { position: 'relative', minHeight: '100vh', background: PAGE }}>
      {!embedded && <div style={{
        position: 'fixed', inset: 0, pointerEvents: 'none', display: 'var(--pal-backdrop)',
        backgroundImage: 'url(/assets/crest/guild-hall-004-hd.webp)', backgroundSize: 'cover', backgroundPosition: 'center',
        filter: 'blur(6px) brightness(0.35) saturate(0.9)', transform: 'scale(1.05)',
      }} />}
      <main style={{ position: 'relative', maxWidth: 1180, margin: '0 auto', padding: embedded ? 0 : '32px 24px 64px', display: 'flex', flexDirection: 'column', gap: 26 }}>
        {!embedded && <header>
          <button type="button" onClick={() => navigate('/hub')} style={{ ...label, background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, padding: 0, marginBottom: 10 }}>
            <ArrowLeft size={12} /> {copy.hubBack}
          </button>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 14, flexWrap: 'wrap' }}>
            <h1 className="pal-title" style={{ margin: 0, fontWeight: TITLE_WEIGHT, fontFamily: TITLE_FONT, fontSize: 'clamp(30px, 5vw, 44px)', letterSpacing: '0.1em', color: ICE, textShadow: arcadeTitleShadow }}>
              {copy.ledger[0]} <SwapWord className="pal-em" word={view === 'stats' ? copy.ledgerStats : copy.ledger[1]} />
            </h1>
            <SkinPicker />
          </div>
        </header>}

        {track === 'readiness' && (
          <ProgressionBoard username={username} readiness={readiness} strategy={strategy} strategies={strategies}
            creating={creatingStrategy}
            onStrategy={code => { setStrategy(code); setCreatingStrategy(false); }}
            onCreate={() => setCreatingStrategy(c => !c)} />
        )}

        {/* Readiness: strategy lives on the board, and stats are a fold-down bar instead of a Log/Stats switch */}
        {track === 'readiness' && (
          <div style={{ background: PANEL, boxShadow: pixelFrame(statsOpen), borderRadius: 'var(--pal-radius, 0)' }}>
            <button type="button" onClick={() => setStatsOpen(o => !o)} aria-expanded={statsOpen}
              style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 18, padding: '14px 18px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', flexWrap: 'wrap' }}>
              <span style={{ fontFamily: TITLE_FONT, fontWeight: 800, fontSize: 18, color: ICE }}>{strategy} stats</span>
              {summary && (
                <span style={{ ...label, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                  <span>{summary.stats.trades} {modeName(mode)} trades</span>
                  <span>win {Math.round(summary.stats.winRate * 100)}%</span>
                  <span style={{ color: summary.stats.expectancyR >= 0 ? WIN : LOSS }}>exp {summary.stats.expectancyR >= 0 ? '+' : ''}{summary.stats.expectancyR.toFixed(2)}R</span>
                </span>
              )}
              <span style={{ ...label, marginLeft: 'auto', color: ACCENT }}>{statsOpen ? '▴ Hide' : '▾ Show stats'}</span>
            </button>
            <div style={{ display: 'grid', gridTemplateRows: statsOpen ? '1fr' : '0fr', transition: 'grid-template-rows 0.4s cubic-bezier(0.22, 1, 0.36, 1)' }}>
              <div style={{ overflow: 'hidden', minHeight: 0 }}>
                <div style={{ padding: '0 18px 18px', display: 'flex', flexDirection: 'column', gap: 20 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
                    {modes.length > 1 && <Segmented value={mode} options={modes} onChange={setMode} format={m => `${modeName(m)} results`} />}
                    {summary && <div style={{ flex: '1 1 420px' }}><RefitBar strategy={strategy} summary={summary} /></div>}
                  </div>
                  {summary && <StatStrip s={summary.stats} />}
                  {statsOpen && <LedgerCharts trades={trades} />}
                  {!trades.length && <div style={{ ...label, textAlign: 'center', padding: '12px 0' }}>No {modeName(mode)} trades for {strategy} yet.</div>}
                </div>
              </div>
            </div>
          </div>
        )}

        {track === 'live' && <RoutineView key={routineVersion} username={username} onEdit={() => setEditingRoutine(true)} />}
        {editingRoutine && <RoutineBuilder username={username} onClose={() => setEditingRoutine(false)} onSaved={() => setRoutineVersion(v => v + 1)} />}

        {track === 'readiness' && (
          <ReadinessImportPanel username={username} onDone={async () => { await refresh(); await loadStrategies(); await loadReadiness(); }} />
        )}

        {/* Strategy is the top-level split (left); Log vs Stats (right); mode toggles inside */}
        {track !== 'readiness' && <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          <nav style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }} aria-label="Strategy">
            {strategies.map(st => (
              <button key={st.code} type="button" title={st.name} onClick={() => { setStrategy(st.code); setCreatingStrategy(false); }}
                style={tab(st.code === strategy && !creatingStrategy)} aria-pressed={st.code === strategy}>{st.code}</button>
            ))}
            <button type="button" onClick={() => setCreatingStrategy(c => !c)} style={tab(creatingStrategy)} aria-label="New strategy" title="New strategy">+</button>
          </nav>
          <nav style={{ display: 'flex', gap: 14 }} aria-label="View">
            {([['log', 'Log'], ['stats', 'Stats']] as ['log' | 'stats', string][]).map(([v, name]) => (
              <button key={v} type="button" onClick={() => setView(v)} style={tab(v === view)} aria-pressed={v === view}>{name}</button>
            ))}
          </nav>
        </div>}

        {creatingStrategy && (
          <NewStrategy
            onClose={() => setCreatingStrategy(false)}
            onCreate={async st => {
              const made = await strategiesApi.create(username, st);
              await loadStrategies();
              setStrategy(made.code);
              setCreatingStrategy(false);
            }}
          />
        )}

        {track !== 'readiness' && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
            {modes.length > 1 && <Segmented value={mode} options={modes} onChange={setMode} format={m => `${modeName(m)} results`} />}
            {summary && <div style={{ flex: '1 1 420px' }}><RefitBar strategy={strategy} summary={summary} /></div>}
          </div>
        )}

        {loadError && (
          <Panel><div style={{ ...label, color: LOSS }}>{loadError} — is the backend running on port 3000?</div></Panel>
        )}

        {view === 'stats' ? (
          <>
            {summary && <StatStrip s={summary.stats} />}
            <LedgerCharts trades={trades} />
            {!trades.length && <Panel><div style={{ ...label, textAlign: 'center', padding: '20px 0' }}>No {modeName(mode)} trades for {strategy} yet.</div></Panel>}
          </>
        ) : (
          <>
            {liveLocked ? (
              <Panel><LiveLock strategy={strategy} r={readiness?.[strategy]} needsTier={needsTier} /></Panel>
            ) : (
            <Panel>
              <LogTradeForm
                strategy={strategy}
                defaultMode={formModes.includes(mode) ? mode : formModes[0]}
                modes={formModes}
                stats={stats}
                chartReader={innerCircle ? { enabled: readerEnabled, read: img => tradesApi.readChart(username, img) } : null}
                onSubmit={async (t, image) => {
                  const saved = await tradesApi.create(username, t);
                  if (image) await tradesApi.uploadImage(username, saved.id, image);
                  // Jump the view to where the trade landed, so a live trade logged from the backtest tab doesn't vanish
                  if (saved.mode !== mode) setMode(saved.mode);
                  else await refresh();
                  setSavedNote(`Saved ${saved.pair} ${modeName(saved.mode)} trade · ${Number(saved.r_multiple) >= 0 ? '+' : ''}${Number(saved.r_multiple).toFixed(2)}R`);
                  if (track === 'readiness') loadReadiness();
                }}
              />
            </Panel>
            )}
            {/* Marathon Method: Inner Circle only; Accelerator sees it locked. On Home Base it has its own row. */}
            {!embedded && <Panel>
              <button
                type="button"
                onClick={() => (innerCircle ? setMarathonOpen(o => !o) : setMarathonLockedNote(true))}
                aria-expanded={innerCircle ? marathonOpen : undefined}
                style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
              >
                <span className="pal-chapter" style={{ fontFamily: TITLE_FONT, fontSize: 22, color: innerCircle ? ICE : TEAL, textShadow: HEAD_SHADOW, display: 'flex', alignItems: 'center', gap: 10 }}>
                  {!innerCircle && <LockIcon size={18} />} Marathon Method
                </span>
                <span style={{ ...label, color: innerCircle ? TEAL : GOLD }}>
                  {innerCircle ? (marathonOpen ? '▾ Hide rotation' : '▸ Rotate accounts') : 'Inner Circle'}
                </span>
              </button>
              {!innerCircle && marathonLockedNote && (
                <div style={{ ...label, color: GOLD, marginTop: 12, lineHeight: 1.6 }}>
                  The Marathon Method rotation tracker is for Inner Circle members. Upgrade to Inner Circle to build and rotate your prop-firm cohorts here.
                </div>
              )}
            </Panel>}
            {!embedded && innerCircle && marathonOpen && <MarathonView username={username} strategies={strategies.map(st => st.code)} />}
            {savedNote && (
              <div role="status" style={{ ...label, color: INK, background: WIN, padding: '10px 14px', boxShadow: `0 0 0 2px ${INK}` }}>{savedNote}</div>
            )}
            <Panel style={{ minWidth: 0 }}>
              <div style={{ marginBottom: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <span className="pal-chapter" style={{ fontFamily: TITLE_FONT, fontSize: 22, color: ICE, textShadow: HEAD_SHADOW }}>History</span>
              </div>
              <TradeTable
                trades={trades}
                onView={async t => setViewing(await tradesApi.imageUrl(username, t.id))}
                onDelete={async t => {
                  if (!window.confirm(`Delete ${t.pair} ${t.mode} trade (${r(Number(t.r_multiple))})? This can't be undone.`)) return;
                  await tradesApi.remove(username, t.id);
                  await refresh();
                }}
              />
            </Panel>
          </>
        )}
      </main>
      {viewing && <Lightbox src={viewing} onClose={() => { URL.revokeObjectURL(viewing); setViewing(null); }} />}
    </div>
  );
}
